import {SerializedError} from '@reduxjs/toolkit'
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query'
import {Permission, permissionRoles} from '../interfaces/user'
import {AuthUser} from '../interfaces/auth'
import {Error, ResponseError} from '../libs/_fri/utils/_models'

export const limitString = (text: string, number: number) => {
  const limit = number || 20
  return text?.toString().length < limit ? text : text?.toString()?.slice(0, limit) + '...'
}

export const parseDateToISOString = (date: Date) => {
  function padTo2Digits(num: number) {
    return num.toString().padStart(2, '0')
  }

  function formatMilliseconds(milliseconds: number) {
    return milliseconds.toString().padStart(3, '0')
  }

  //yyyy-mm-ddThh:mm:ss.msZ
  return `${date.getFullYear()}-${padTo2Digits(date.getMonth() + 1)}-${padTo2Digits(
    date.getDate()
  )}T${padTo2Digits(date.getHours())}:${padTo2Digits(date.getMinutes())}:${padTo2Digits(
    date.getSeconds()
  )}.${formatMilliseconds(date.getMilliseconds())}Z`
}

export const formatDate = (date: Date | undefined): string => {
  if (!date) return ''

  const year = date.getFullYear()

  const month = ('0' + (date.getMonth() + 1)).slice(-2)

  const day = ('0' + date.getDate()).slice(-2)

  return `${year}-${month}-${day}`
}

export const removeStorage = (key: string): Boolean => {
  try {
    localStorage.setItem(key, '')
    localStorage.setItem(key + '_expiresIn', '')
  } catch (e) {
    console.log(
      'removeStorage: Error removing key [' + key + '] from localStorage: ' + JSON.stringify(e)
    )
    return false
  }
  return true
}

export const setStorage = (key: string, value: any, expires: number) => {
  if (!localStorage) {
    return
  }
  if (expires === undefined || expires === null) {
    expires = 24 * 60 * 60 // default: seconds for 1 day
  }

  const now = Date.now() //millisecs since epoch time, lets deal only with integer
  const schedule = JSON.stringify(now + expires * 1000)

  try {
    const lsValue = JSON.stringify(value)
    localStorage.setItem(key, lsValue)

    localStorage.setItem(key + '_expiresIn', schedule)
  } catch (error) {
    console.log(
      'setStorage: Error setting key [' + key + '] in localStorage: ' + JSON.stringify(error)
    )
    return false
  }

  return true
}

export const getStorage = <T>(key: string): T | null => {
  if (!localStorage) {
    return null
  }

  const now = Date.now()
  let expiresIn = Number(localStorage.getItem(key + '_expiresIn'))
  if (expiresIn === undefined || expiresIn === null) {
    expiresIn = 0
  }

  expiresIn = Math.abs(expiresIn)
  if (expiresIn < now) {
    // Expired
    removeStorage(key)
    return null
  }

  try {
    const value: string | null = localStorage.getItem(key)
    if (!value) {
      return null
    }

    const result: T = JSON.parse(value) as T
    if (result) {
      return result
    } else {
      return null
    }
  } catch (e) {
    console.log(
      'getStorage: Error reading key [' + key + '] from localStorage: ' + JSON.stringify(e)
    )
    return null
  }
}

export const getLink = (data: string) => {
  if (!data) return
  const uint8Array = new Uint8Array(JSON.parse(data))
  const blob = new Blob([uint8Array], {type: 'application/octet-stream'})
  const url = window.URL.createObjectURL(new Blob([blob]))
  return url
}

export const roundedTime = (date: Date) => {
  const selectedDate = new Date(date.getTime()) // Clonamos la fecha para evitar mutar el argumento original
  const minutes = selectedDate.getMinutes()

  if (minutes > 30) {
    selectedDate.setHours(selectedDate.getHours() + 1)
    selectedDate.setMinutes(0)
  } else if (minutes > 0 && minutes <= 30) {
    selectedDate.setMinutes(30)
  }

  return selectedDate
}

export const getOppositeStatus = (status: 'active' | 'blocked'): 'active' | 'blocked' =>
  status === 'active' ? 'blocked' : 'active'

export function hasPermission(user: AuthUser | null, permission: Permission): boolean {
  if (!user) return false
  return (user.type === 'admin' || permissionRoles.get(permission)?.includes(user.type)) ?? false
}

export function getOptionsRoles(user: AuthUser | null): {value: string; label: string}[] {
  if (!user || user.type !== 'admin') return []
  return [
    {value: 'admin', label: 'Administrador'},
    {value: 'authorizer', label: 'Autorizador'},
    {value: 'operative', label: 'Operativo'},
  ]
}
